import React, { useState, useEffect } from "react";
import HeaderComponent from "@components/common/Header/Header";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import { useNavigate } from "react-router-dom";
import { TextField } from "@mui/material";
import FilterIcon from "@styles/all-images/icons/rejectIcon.svg";
import ElipsedText from "@/components/common/elipsis/ElipsedText";
import CheckIcon from "@styles/all-images/icons/blue-tick.svg";
import ChevronDownIcon from "@styles/all-images/icons/chevron-down-grey.svg";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Include Quill's CSS
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import { toast } from "react-toastify";

interface UserType {
  id: string;
  user_type: string;
}

const AddCMSPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppThunkDispatch>();
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [content, setContent] = useState("");
  const [categories, setCategories] = useState<UserType[]>([]);

  const fetchUserTypes = async () => {
    try {
      const { unAuthenticatedCall: getUserTypes } =
        actionsHandler("userTypeList");
      const response = await dispatch(
        getUserTypes({
          method: "get",
        }),
      );

      if (response.ok && response.data) {
        setCategories(
          response.data.map((item: any) => ({
            id: item.id,
            user_type: item.user_type,
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching user types:", error);
    }
  };

  useEffect(() => {
    fetchUserTypes();
  }, []);

  const filterOptions = {
    options: categories.map((cat) => cat.user_type),
  };

  const handleChange = (value: string) => {
    setContent(value);
  };

  const handleFilterSelect = (option: string) => {
    setSelectedFilter(option === selectedFilter ? null : option);
  };

  const createSlug = (title: string): string => {
    return title.toLowerCase().replace(/\s+/g, "-"); // Replace spaces with hyphens
  };

  const handleSave = async () => {
    try {
      const { unAuthenticatedCall: createCMSPage } =
        actionsHandler("cmsContent");

      const titleInput = document.querySelector(
        'input[name="name"]',
      ) as HTMLInputElement;
      const selectedCategory = categories.find(
        (cat) => cat.user_type === selectedFilter,
      );

      const payload = {
        title: titleInput?.value,
        content: content,
        user_type: selectedCategory?.id,
        slug: createSlug(titleInput?.value || ""),
        is_archive: false,
      };

      const response = await dispatch(
        createCMSPage({
          method: "post",
          payload,
        }),
      );

      if (response.ok) {
        toast.success(response.data.message || "CMS Page created successfully");
        navigate(-1);
      } else {
        if (Array.isArray(response.data.message)) {
          response.data.message.forEach((message: string) => {
            toast.error(message);
          });
        } else {
          toast.error(response.data.message || "An unexpected error occurred");
        }
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An unexpected error occurred");
    }
  };

  return (
    <div className="tw-relative">
      <HeaderComponent />
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-left-0 tw-right-0 tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <button
                onClick={() => navigate(-1)}
                className="tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer hover:tw-opacity-80"
              >
                <img
                  src={BackIcon}
                  alt="Back Icon"
                  className="tw-cursor-pointer tw-w-6 tw-h-6"
                />
              </button>
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                Add CMS Page
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="tw-mt-[147px] tw-px-8 tw-pb-8">
        <div className="tw-p-6">
          <div className="tw-bg-white tw-border tw-rounded-2xl tw-border-[#E9EAEB] tw-mx-[178px] tw-p-8">
            <div>
              <div className="tw-grid lg:tw-grid-cols-3 md:tw-grid-cols-2 lg:tw-gap-6 md:tw-gap-4 tw-gap-2">
                <div className="form-group tw-mb-6">
                  <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                    User Type (Tier)
                  </p>
                  <div className="tw-relative tw-w-64">
                    <button
                      onClick={() => setIsFilterOpen(!isFilterOpen)}
                      className="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-shadow-sm"
                    >
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <img
                          src={FilterIcon}
                          alt="Filter Icon"
                          className="tw-w-5 tw-h-5"
                        />
                        <span className="tw-text-[#717680]">
                          {selectedFilter
                            ? `${selectedFilter} selected`
                            : "Select Filter"}
                        </span>
                      </div>
                      <img
                        src={ChevronDownIcon}
                        alt="Chevron Down"
                        className={`tw-w-5 tw-h-5 tw-transition-transform ${isFilterOpen ? "tw-rotate-180" : ""}`}
                      />
                    </button>

                    {isFilterOpen && (
                      <div className="tw-absolute tw-z-20 tw-w-full tw-mt-1 tw-bg-white tw-border tw-border-[#F5F5F5] tw-rounded-lg tw-shadow-lg">
                        <div className="">
                          {filterOptions.options.map((option) => (
                            <button
                              key={option}
                              className="tw-w-full tw-flex tw-items-center tw-justify-between tw-p-2 hover:tw-bg-[#FAFAFA] tw-rounded"
                              onClick={() => handleFilterSelect(option)}
                            >
                              <span className="tw-text-[#717680]">
                                <ElipsedText text={option} />
                              </span>
                              {selectedFilter === option && (
                                <img
                                  src={CheckIcon}
                                  alt="Check Icon"
                                  className="tw-w-5 tw-h-5"
                                />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group tw-mb-6">
                  <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                    Title
                  </p>
                  <TextField
                    fullWidth
                    type="text"
                    name="name"
                    variant="outlined"
                    placeholder="Enter Document Title"
                    className="tw-mb-4"
                  />
                </div>
              </div>
              <div className="text-editor-container">
                <ReactQuill
                  theme="snow"
                  value={content}
                  onChange={handleChange}
                  placeholder="Start typing here..."
                  className="tw-h-[416px] tw-pb-10"
                />
              </div>
            </div>
          </div>
          <div className="tw-flex tw-justify-center tw-mt-4">
            <button
              onClick={handleSave}
              className="tw-flex tw-items-center tw-gap-2 tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCMSPage;
